import { cartState, INTERNAL_WRITE_HEADER } from "./cart-state";
import { openDrawer } from "./cart-drawer";

/**
 * Third-party widgets (e.g. `fs-buy-now-form`) sometimes write to the cart by
 * POSTing to `/cart/add.js` directly, outside `cartState`. There is no
 * cooperative API to hook for that — the request itself is the only signal —
 * so this watches `fetch` and `XMLHttpRequest` for a successful POST to
 * `/cart/add(.js)` and, when one lands that we did not make ourselves,
 * refreshes the drawer and bubble the same way `cartState.add()` would.
 *
 * Our own writes are marked with `INTERNAL_WRITE_HEADER` in
 * `cart-state.ts#requestInit` and are ignored here to avoid a redundant
 * refresh.
 */

const ADD_PATH = /\/cart\/add(\.js)?(\?|$)/;

function isCartAddUrl(url: string): boolean {
  try {
    return ADD_PATH.test(new URL(url, window.location.origin).pathname);
  } catch {
    return ADD_PATH.test(url);
  }
}

function hasInternalMarker(headers: HeadersInit | undefined): boolean {
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has(INTERNAL_WRITE_HEADER);
  const entries = Array.isArray(headers) ? headers : Object.entries(headers);
  return entries.some(
    ([key]) => key.toLowerCase() === INTERNAL_WRITE_HEADER.toLowerCase(),
  );
}

async function onExternalAdd(): Promise<void> {
  await cartState.refresh();
}

function patchFetch(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof window.fetch>) => {
    const [input, init] = args;
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const headers =
      init?.headers ?? (input instanceof Request ? input.headers : undefined);

    const watch =
      method === "POST" && isCartAddUrl(url) && !hasInternalMarker(headers);

    const response = await originalFetch(...args);
    if (watch && response.ok) void onExternalAdd();
    return response;
  };
}

function patchXhr(): void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const watched = new WeakSet<XMLHttpRequest>();

  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest,
    ...args: Parameters<XMLHttpRequest["open"]>
  ) {
    const [method, url] = args;
    if (method.toUpperCase() === "POST" && isCartAddUrl(String(url))) {
      watched.add(this);
    } else {
      watched.delete(this);
    }
    return originalOpen.apply(this, args);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest,
    ...args: Parameters<XMLHttpRequest["send"]>
  ) {
    if (watched.has(this)) {
      this.addEventListener("loadend", () => {
        if (this.status >= 200 && this.status < 300) void onExternalAdd();
      });
    }
    return originalSend.apply(this, args);
  };
}

export function installCartWriteDetection(): void {
  patchFetch();
  patchXhr();

  // `window.BondiCart.openDrawer` is documented for non-module scripts (see
  // `assets/swym-wishlist.js`) but had nothing assigning it; wire it up here,
  // where both pieces are already imported without a circular dependency.
  if (window.BondiCart) window.BondiCart.openDrawer = openDrawer;
}
